import { Boarding } from 'boarding.js'
import 'boarding.js/styles/main.css'
import 'boarding.js/styles/themes/basic.css'
import { isObject } from 'lodash-es'
import moment from 'moment'

import {
  BoardingElement,
  databaseBoardingFlow,
  IdeAdvanceCompilerBoardngFlow,
  IdeFlowType,
  IdeOnboardngFlow,
  IdeOnboardngMobileFlow,
  type OnboardingStep
} from '@/utils/ideBoardingSteps'

import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'

import tabsService from '@/services/ide/tabs.service'
import { IDEVIEWTABS_MOBILE, type TAB_ICONS_NAMES } from '@/utils/tabs'

interface IBoardingItem {
  ideType: string
  isBoarded: boolean
}

interface IBoardingData {
  storeBoardingArray: IBoardingItem[]
  boardingModal: boolean
}

export enum IDEType {
  IDE = 'IDE',
  AdvanceCompilerIDE = 'AdvanceCompilerIDE',
  Database = 'Database'
}

export enum BOARDING_CONSTANT {
  BOARDING_DATA = 'boardingData'
}

const boardingData: IBoardingData = {
  storeBoardingArray: [
    {
      ideType: IDEType.IDE,
      isBoarded: false
    },
    {
      ideType: IDEType.AdvanceCompilerIDE,
      isBoarded: false
    },
    {
      ideType: IDEType.Database,
      isBoarded: false
    }
  ],
  boardingModal: false
}

/**
 * Updates the isBoarded value in storeBoardingArray dynamically based on ideType
 *  @param isBoardingModal - check to open  the modal
 */
const setTypeAndBoardingSteps = (isBoardingModal: boolean = false) => {
  switch (true) {
    case useIdeStore().isAdvancedCompiler:
      updateBoardingStatus(IDEType.AdvanceCompilerIDE, isBoardingModal)
      break
    case useIdeStore().isAdvancedCompilerDatabase:
      updateBoardingStatus(IDEType.Database, isBoardingModal)
      break
    case !useIdeStore().isAdvancedCompiler && !useIdeStore().isAdvancedCompilerDatabase:
      updateBoardingStatus(IDEType.IDE, isBoardingModal)
      break
    default:
  }
}

/**
 * Updates the isBoarded value to true for the specified ideType in the array
 * @param ideType - The IDE type to update
 * @param isBoardingModal - check to open  the modal
 */
const updateBoardingStatus = (ideType: string | null, isBoardingModal: boolean = false) => {
  const data = getBoardingSteps()
  const clonedData = { ...data }
  clonedData.storeBoardingArray = [...(data.storeBoardingArray || boardingData.storeBoardingArray)]
  if (ideType) {
    const item = clonedData.storeBoardingArray.find((entry) => entry.ideType === ideType)
    if (item) {
      item.isBoarded = true
    }
  }

  const anyBoarded = clonedData.storeBoardingArray.some((entry) => entry.isBoarded)
  clonedData.boardingModal = isBoardingModal ? isBoardingModal : anyBoarded

  localStorage.setItem(BOARDING_CONSTANT.BOARDING_DATA, JSON.stringify(clonedData))
}

/**
 * Retrieves the boarding steps array from localStorage.
 * @returns The parsed array from localStorage or an empty array.
 */
const getBoardingSteps = (): Partial<IBoardingData> => {
  const boardingData = localStorage.getItem(BOARDING_CONSTANT.BOARDING_DATA)
  return JSON.parse(boardingData as string) ?? {}
}

/**
 * Checks the conditions and logs if the `isBoarded` flag is true and the condition matches.
 * @returns the onBoarder flag value
 */
const checkBoardingStatusAndConditions = () => {
  const boardingArray = (getBoardingSteps().storeBoardingArray || []) as IBoardingItem[]

  const boardingLookup = boardingArray.reduce((acc, entry: any) => {
    acc[entry.ideType] = entry.isBoarded
    return acc
  }, {} as Record<string, boolean>)

  switch (true) {
    case !useIdeStore().isAdvancedCompiler && !useIdeStore().isAdvancedCompilerDatabase:
      if (!boardingLookup[IDEType.IDE]) {
        return true
      }
      break
    case useIdeStore().isAdvancedCompiler:
      if (!boardingLookup[IDEType.AdvanceCompilerIDE]) {
        return true
      }
      break
    case useIdeStore().isAdvancedCompilerDatabase:
      if (!boardingLookup[IDEType.Database]) {
        return true
      }
      break
    default:
      return false
  }
}

/**
 * checking the steps according to ide type
 * @returns returns the filtered array
 */
const checkBoardingStep = async () => {
  switch (true) {
    case useIdeStore().isAdvancedCompiler:
      return postBoardingInit(IdeAdvanceCompilerBoardngFlow)
    case useIdeStore().isAdvancedCompilerDatabase:
      return postBoardingInit(databaseBoardingFlow)
    case !useIdeStore().isAdvancedCompiler && !useIdeStore().isAdvancedCompilerDatabase:
      if (useIdeStore().currentMobileTab) {
        return postBoardingInit(IdeOnboardngMobileFlow)
      } else return postBoardingInit(IdeOnboardngFlow)
    default:
      return []
  }
}
/**
 * checking the the ace is initialized
 * @param item - the steps array
 * @param count - count
 * @returns - the array with filtered steps
 */
const postBoardingInit = async (item: OnboardingStep[], count = 0) => {
  if (!useIdeStore().isWindowAce() || !useAuthStore().isInitiated) {
    if (count > 10) {
      return []
    } else {
      await new Promise((resolve) => setTimeout(resolve, 600))
      postBoardingInit(item, count + 1)
    }
  } else {
    return filterAndAddDynamicLogic(item)
  }
}

/**
 * Checking the item object to see if condition exists
 * @param item - take the  item
 * @returns - boolean
 */
const checkConditions = (item: any): boolean => {
  return item.conditionType.reduce((exists: boolean, type: string) => {
    switch (type) {
      case BoardingElement.IDE_LIB_TAB:
        return exists || (useIdeStore().ideMeta?.isPlus && useIdeStore().ideMeta?.library)
      case BoardingElement.IDE_DATABSE_OPEN:
        return exists || useIdeStore().setMobileTab(IDEVIEWTABS_MOBILE.IDE)
      default:
        return exists || document.getElementById(type as string)
    }
  }, false)
}

/**
 * filter the steps with the id
 * @param flowArray - the steps array
 * @returns - the array with filtered steps
 */
const filterAndAddDynamicLogic = (flowArray: OnboardingStep[]) => {
  const tempArray: OnboardingStep[] = []

  flowArray.forEach((item: any, index: any) => {
    const conditionExists = checkConditions(item)

    if (conditionExists) {
      const tempItem = { ...item }

      let nextItem = flowArray[index + 1]
      let nextConditionExists = nextItem && checkConditions(nextItem)

      let nextIndex = index + 1
      while (!nextConditionExists && nextIndex < flowArray.length - 1) {
        nextIndex++
        nextItem = flowArray[nextIndex]
        nextConditionExists = nextItem && checkConditions(nextItem)
      }

      let prevItem = flowArray[index - 1]
      let prevConditionExists = prevItem && checkConditions(prevItem)
      let prevIndex = index - 1
      while (!prevConditionExists && prevIndex > 0) {
        prevIndex--
        prevItem = flowArray[prevIndex]
        prevConditionExists = prevItem && checkConditions(prevItem)
      }

      if (nextConditionExists && nextItem?.idType === IdeFlowType.TAB && isObject(nextItem)) {
        tempItem.onNext = function () {
          const tabMobileId = nextItem.tabMobileId
          if (useIdeStore().currentMobileTab) {
            useIdeStore().setMobileTab(tabMobileId as string)
            useIdeStore().boardingInstance?.preventMove()
          } else {
            const tabName = nextItem.tabName
            tabsService.updateTabVisiblity(tabName as TAB_ICONS_NAMES)
            useIdeStore().boardingInstance?.preventMove()
          }
          useIdeStore().boardingInstance?.continue()
        }
      }

      if (
        prevConditionExists &&
        tempArray.length !== 0 &&
        prevItem?.idType === IdeFlowType.TAB &&
        isObject(prevItem)
      ) {
        tempItem.onPrevious = function () {
          const tabMobileId = prevItem.tabMobileId
          if (useIdeStore().currentMobileTab) {
            useIdeStore().setMobileTab(tabMobileId as string)
            useIdeStore().boardingInstance?.preventMove()
          } else {
            const tabName = prevItem.tabName
            tabsService.updateTabVisiblity(tabName as TAB_ICONS_NAMES, true)
            useIdeStore().boardingInstance?.preventMove()
          }
        }
      }

      tempItem.prepareElement = function () {
        const currentElementId = item.element.replace('#', '')
        if (!document.getElementById(currentElementId)) {
          useIdeStore().boardingInstance?.continue()
        }
      }
      tempArray.push(tempItem)
    }
  })

  return tempArray
}

/**
 * Initializing the boarding steps
 */
const initializedBoardingSteps = async () => {
  setTypeAndBoardingSteps()
  useIdeStore().boardingInstance = new Boarding({
    allowClose: false,
    strictClickHandling: 'block-all',
    className: 'scoped-class',
    closeBtnText: 'Skip',
    opacity: 0.5,
    onPopoverRender: function (popoverElements: any) {
      setTimeout(() => {
        if (useIdeStore().boardingInstance) {
          const currentStep = useIdeStore().boardingInstance?.currentStep as number
          const totalSteps = useIdeStore().boardingInstance?.getSteps().length
          popoverElements.popoverTitle.innerHTML = `${
            popoverElements.popoverTitle.innerText
          }  <span class="step-number">Step ${currentStep + 1} of ${totalSteps}</span>`
        }
      }, 0)
    }
  })
  const checkArray = await checkBoardingStep()
  if (checkArray && Array.isArray(checkArray)) {
    useIdeStore().boardingInstance?.defineSteps(checkArray)
    useIdeStore().boardingInstance?.start()
  }
}

/**
 * check the time between the release and till when to show the modal
 * @param startDate - take the date from when you want show
 * @param weeksAhead - number of weekends you want to show
 * @returns - if the time is within the given time return boolean value
 */
const setTimeToShowBoardingTill = (startDate: string, weeksAhead: number): boolean => {
  // staring date should be passed like '2024-11-21'
  const start = moment(startDate).startOf('day')
  const end = start.clone().add(weeksAhead, 'weeks').endOf('day')

  const now = moment().startOf('day')
  return now.isBetween(start, end, null, '[]')
}

export default {
  setTypeAndBoardingSteps,
  checkBoardingStatusAndConditions,
  checkBoardingStep,
  initializedBoardingSteps,
  getBoardingSteps,
  setTimeToShowBoardingTill,
  updateBoardingStatus
}
